import React, { useState, useEffect } from 'react'
import { ModalProps } from 'components/modal'
import Dialog from 'components/dialog'
import { BLUE_BUTTON } from 'components/save-buttons'
import { useProject } from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { ITableViewWithColumns } from 'types'
import { IColumnTypes } from 'components/spreadsheet/types'
import { updateColumn } from 'components/spreadsheet/contexts/data/actions'
import Button from 'components/button'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import api from 'helpers/api'
import { HUB_BASE } from 'routes'
import { COLUMN_TYPE_NAMES, DATE_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { detectLikelyDateFormat, detectLikelyDatetimeFormat } from 'components/spreadsheet/helpers/date'
import { getRowValue } from 'components/spreadsheet/helpers/functions'
import { summariseKindChange } from 'components/spreadsheet/helpers/columnKindChange'

const KindChangeWarning: React.FC<{ previousKind: IColumnTypes | undefined; newKind: IColumnTypes }> = ({
  previousKind,
  newKind
}) => {
  if (!previousKind) {
    return (
      <>
        Are you sure you want to change the kind of this column? There is a chance for{' '}
        <b className="text-red">data to be lost</b> in all of the cells in this column.{' '}
      </>
    )
  }

  const { outcome, detail } = summariseKindChange(previousKind, newKind)
  const change = (
    <>
      Changing this column from <b>{COLUMN_TYPE_NAMES[previousKind]}</b> to <b>{COLUMN_TYPE_NAMES[newKind]}</b>
    </>
  )

  if (outcome === 'cleared') {
    return (
      <>
        {change} will <b className="text-red">clear every value</b> in it. This cannot be undone.{' '}
      </>
    )
  }

  if (outcome === 'partial') {
    return (
      <>
        {change} will <b className="text-red">change or clear some values</b>. {detail} This cannot be undone.{' '}
      </>
    )
  }

  return (
    <>
      {change} keeps every value. {detail}{' '}
    </>
  )
}

export interface ColumnKindModalProps extends ModalProps {
  newKind: IColumnTypes
  columnId: string | undefined
  onCancel: () => void
}

const ColumnKindModal: React.FC<ColumnKindModalProps> = ({ open, setOpen, newKind, columnId, onCancel }) => {
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage } = useApplicationStore()

  const [processing, setProcessing] = useState<boolean>(false)
  const [usedViews, setUsedViews] = useState<ITableViewWithColumns[]>([])

  const column = spreadsheetData.viewDetails.columns.find((col) => col.publicId === columnId)

  const handleClose = () => {
    setOpen(false)
    setProcessing(false)
  }

  const handleFailure = (error: any) => {
    displayErrorMessage(error)
    setProcessing(false)
  }

  const handleUpdateColumn = () => {
    if (columnId) {
      let alterOptions = undefined

      // If the new kind is date or datetime then try to detect the format
      // for alter options
      if (DATE_COLUMN_TYPES.includes(newKind)) {
        if (column) {
          const allValues: string[] = []
          for (const row of spreadsheetData.rows) {
            const value = getRowValue(
              row,
              column.publicId,
              spreadsheetData.columnIdToIndex,
              spreadsheetData.columnStore
            )
            if (value) allValues.push(value as string)
          }

          if (newKind === 'date') {
            alterOptions = { dateConversionFormat: detectLikelyDateFormat(allValues) }
          }

          if (newKind === 'datetime') {
            const likelyFormat = detectLikelyDatetimeFormat(allValues)
            alterOptions = { dateConversionFormat: likelyFormat }
          }
        }
      }

      setProcessing(true)
      updateColumn(
        project.publicId,
        'kind',
        spreadsheetData,
        setSpreadsheetData,
        columnId,
        newKind,
        handleClose,
        handleFailure,
        alterOptions
      )
    }
  }

  useEffect(() => {
    if (open && columnId) {
      if (columnId) {
        api
          .getColumnUsedViews(spreadsheetData.tableDetails.publicId, columnId)
          .then((response) => {
            setUsedViews(response.data)
          })
          .catch((error) => {
            displayErrorMessage(error)
          })
      }
    }
  }, [open])

  const getUsedViewFilters = () => {
    const usedViewFilters = []

    for (let i = 0; i < usedViews.length; i++) {
      let include = false
      const view = usedViews[i]
      for (let j = 0; j < view.filterSettings.length; j++) {
        const filter = view.filterSettings[j]
        if (filter.columnId === columnId) include = true
      }

      if (include) [usedViewFilters.push(view)]
    }

    return usedViewFilters
  }

  const usedViewFilters = getUsedViewFilters()

  // Red only for a change that clears or alters values (Select to JSON, say); one that keeps every value (Select to
  // Text) is the product's blue.
  const losesValues = !column?.kind || summariseKindChange(column.kind, newKind).outcome !== 'kept'

  // Closing it any other way than Change Kind keeps the column as it is.
  const keepCurrentKind = () => {
    if (processing) return
    setOpen(false)
    onCancel()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && keepCurrentKind()}
      title="Confirm Column Change"
      width="small"
      // Over Edit column, which stays open behind it.
      nestedInMenu
      footer={
        <>
          <Button internalType="outline" disabled={processing} onClick={keepCurrentKind}>
            Keep Current Kind
          </Button>
          <Button
            data-tour="confirm-change-column-kind"
            internalType={losesValues ? 'danger' : 'outline'}
            className={losesValues ? undefined : BLUE_BUTTON}
            isLoading={processing}
            onClick={() => {
              if (!processing) handleUpdateColumn()
            }}
          >
            Change Kind
          </Button>
        </>
      }
    >
      <p className="text-pretty">
        <KindChangeWarning previousKind={column?.kind} newKind={newKind} />
      </p>
      {usedViewFilters.length > 0 && (
        <>
          <p className="mt-3 text-pretty">
            Also, <b className="text-red">all filter settings related to this column will be removed</b> from the
            following views in this table:
          </p>
          <ul className="mt-2 flex flex-col gap-1 pl-4 list-disc">
            {usedViewFilters.map((view: ITableViewWithColumns) => (
              <li key={view.publicId}>
                <a
                  href={`${HUB_BASE}${project.publicId}/table/${spreadsheetData.tableDetails.publicId}/view/${view.publicId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue hover:underline"
                >
                  {view.name}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </Dialog>
  )
}

export default ColumnKindModal
